import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useOutletContext } from 'react-router-dom'
import { Scrollama, Step } from 'react-scrollama'
import ReactPlayer from 'react-player'
import { getScrollerById } from '../content'

const learnMoreContent = (
  <div className="learn-more-container">
    <h2>Learn More</h2>
    <div className="learn-more-grid">
      {[
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image4.png",
          alt: "Climate Awareness 1",
          title: "How Climate Change is Quietly Rewriting Bhutan’s Future",
          link: 'https://www.bbc.co.uk/teach/class-clips-video/articles/zg4ntrd',
          description: "Effects of rising temperatures on the glaciers there, and the danger this poses.",
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image6.jpg",
          alt: "Climate Awareness 2",
          title: "Scientists Looked Deep Underneath the Doomsday Glacier",
          description: "What they found spells potential disaster for the planet.",
          link: 'https://edition.cnn.com/2024/09/20/climate/doomsday-glacier-thwaites-melt-sea-level-rise/index.html'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image5.png",
          alt: "Climate Awareness 3",
          title: "Millions of Children Affected by Devastating Flooding in South Asia",
          description: "Though flooding at this period of the year is common, this widespread scale of floods during mid-July is unusual.",
          link: 'https://www.unicef.org/press-releases/millions-children-affected-devastating-flooding-south-asia-many-more-risk-covid-19'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image8.png",
          alt: "Climate Awareness 4",
          title: "Living in Constant Fear of Glacial Lake Outburst Flood",
          description: "A story of Lunana, a remote highland community in Bhutan, nestled among glaciers, where climate change is an everyday reality.",
          link: 'https://undp-bhutan.exposure.co/living-in-constant-fear-of-glacial-lake-outburst-flood'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image7.png",
          alt: "Climate Awareness 5",
          title: "Glacial Lake Floods Prevented in Bhutan",
          description: "Every year from June to September, approximately 200-250 people trek to Thorthomi glacial lake to increase controlled water drainage from the lake using pickaxes and shovels.",
          link: 'https://www.youtube.com/watch?app=desktop&v=lbgv4ZMF5Zk&ab_channel=UNDPinAsiaandthePacific'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image2.png",
          alt: "Climate Awareness 6",
          title: "Dwindling Cordyceps Sparks Fear of a Bleak Future for Lunana Women ",
          description: "Lunana has warmed up a lot in the last decade. I think this is wiping out Cordyceps.",
          link: 'https://undp-bhutan.exposure.co/climate-change-exacerbates-lunana-womens-insecurity'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image1.png",
          alt: "Climate Awareness 7",
          title: "A Research Team Travels to Bhutan to Train Experts in Analysing Climate Risks",
          description: "And returns with the insight that the Himalayan state has a lot to teach.",
          link: 'https://www.eurac.edu/en/magazine/climate-risks-the-case-of-bhutan'
        },
        {
          image: "https://s3.ap-southeast-1.amazonaws.com/thegirlandherpiano.com/assets/climate-awareness/image3.png",
          alt: "Climate Awareness 8",
          title: "Melting Glaciers in Bhutan",
          description: "Ade Adepitan visits Bhutan to see the effects of rising temperatures on the glaciers there.",
          link: 'https://www.bbc.co.uk/teach/class-clips-video/articles/zg4ntrd'
        },
      ].map((item, index) => (
        <div key={index} className="learn-more-item">
          <img src={item.image} alt={item.alt} className="learn-more-image" />
          <div className="learn-more-content">
            <h3 className="learn-more-title">{item.title}</h3>
            <p className="learn-more-description">{item.description}</p>
            <a href={item.link} target="_blank" rel="noopener noreferrer" className="learn-more-link">Learn More</a>
          </div>
        </div>
      ))}
    </div>
  </div>
)

const parseStepContent = (step) => {
  const content = step.Content
  const parsed = content.map((item) => {
    switch (item.type) {
      case 'heading':
        const HeadingTag = `h${item.level}`;
        if (item.children[0].text !== '') {
          return <HeadingTag key={item.children[0].text}>{item.children[0].text}</HeadingTag>;
        }
        return null;
      case 'paragraph':
        if (item.children[0].code === true) {
          return <div key={item.children[0].text} dangerouslySetInnerHTML={{ __html: item.children[0].text }} />
        }
        else if (item.children[0].text !== '') {
          return <p key={item.children[0].text}>{item.children[0].text}</p>;
        }
        return null;
      default:
        return null;
    }
  }).filter(item => item !== null)
  if (parsed.length === 0) {
    return null
  } else {
    return (
      <div className={`step-content ${step.DarkTextBackdrop ? 'with-backdrop' : ''}`}>
      {parsed}
      </div>
    )
  }
};

const Scroller = () => {
  const dispatch = useDispatch()
  const { scrollerId } = useParams()
  const { scroller, scrollers } = useSelector((state) => state.content)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState([])
  const [fixedContentId, setFixedContentId] = useState(null)
  const { isMobile, isLandscape } = useOutletContext()
  const [progress, setProgress] = useState(0)
  const scrollerContainerRef = useRef(null)
  const [isScrollamaComplete, setIsScrollamaComplete] = useState(false)

  const onStepEnter = ({ data }) => {
    setCurrentStepIndex(data)
    const currentStep = steps.find(s => s.StepID === data)
    if (currentStep && currentStep.ContentStepOverride) {
      setFixedContentId(currentStep.ContentStepOverride)
    } else {
      setFixedContentId(null)
    }
  }

  const onStepProgress = ({ progress }) => {
    setProgress(progress)
  }

  useEffect(() => {
    if (progress >= 1 && currentStepIndex >= steps.length) {
      setIsScrollamaComplete(true)
    } else {
      setIsScrollamaComplete(false)
    }
  }, [progress, currentStepIndex, steps.length])

  useEffect(() => {
    if (isScrollamaComplete) {
      document.body.style.overflow = 'auto'
      if (scrollerContainerRef.current) {
        scrollerContainerRef.current.style.height = 'auto'
      }
    }
  }, [isScrollamaComplete])

  useEffect(() => {
    if (scrollerId) {
      const scroller = scrollers.find((scroller) => scroller.URL === scrollerId)
      if (scroller && scroller.documentId) {
        dispatch(getScrollerById(scroller.documentId))
      }
    }
  }, [scrollerId, dispatch, scrollers])

  useEffect(() => {
    if (scroller) {
      setSteps(scroller?.Step || [])
    }
  }, [scroller])

  const isCurrentStep = (step) => {
    const currentStep = steps.find(s => s.StepID === currentStepIndex)
    if (currentStep && currentStep.VideoStepOverride) {
      return step.StepID === currentStep.VideoStepOverride
    }
    return currentStepIndex === step.StepID
  }

  const renderStepContent = (step) => (
    <div className={`step-content-container ${step.ContentAlignment ? `content-${step.ContentAlignment}` : ''}`}>
      {step.Title && <h1>{step.Title}</h1>}
      {step.Subtitle && <h2>{step.Subtitle}</h2>}
      {step.Content && parseStepContent(step)}
    </div>
  )

  return (
    <>
    <div className='scroller-container' ref={scrollerContainerRef} style={{ pointerEvents: isScrollamaComplete ? 'none' : 'auto' }}>
      <Scrollama 
        onStepEnter={onStepEnter} 
        onStepProgress={onStepProgress} 
        offset={0.5}
        style={{ 
          position: isScrollamaComplete ? 'relative' : 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
        }}
      >
        <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 10, display: 'none' }}>Step Index: {currentStepIndex}</div>
        <div
          className='scroller-video-container'
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            zIndex: 1,
            overflow: 'hidden'
        }}>
          {steps.map((step, index) => (
            <div
              key={step.StepID}
              className={`step-id-${step.stepID} step-index-${index + 1}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: isCurrentStep(step) && !isScrollamaComplete ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              {step.VideoURL || step.PortraitVideoURL ? (
                <ReactPlayer
                  url={isMobile && !isLandscape && step.PortraitVideoURL ? step.PortraitVideoURL : step.VideoURL}
                  width='100%'
                  height='100%'
                  playing={isCurrentStep(step)}
                  loop
                  muted
                  playsinline
                  config={{
                    file: {
                      attributes: {
                        preload: 'auto',
                        autoPlay: true,
                      },
                    },
                  }}
                />
              ) : (step.ImageURL || step.PortraitImageURL) ? (
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${isMobile && !isLandscape && step.PortraitImageURL ? step.PortraitImageURL : step.ImageURL})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
              ) : null}
            </div>
          ))}
        </div>
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {steps.map((step, index) => (
            <div 
              key={step.StepID}
              className={`step-id-${step.stepID} step-index-${index + 1} fixed-content`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: fixedContentId === step.StepID && !isScrollamaComplete ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
              }}
            >
              {renderStepContent(step)}
            </div>
          ))}
        </div>
        {steps.map((step, index) => (
          <Step data={step.StepID} key={step.StepID}>
            <div
              className={`step-id-${step.stepID} step-index-${index + 1}`}
              style={{ 
              minHeight: '100vh', 
              // display: 'flex', 
              // alignItems: 'center', 
              // justifyContent: 'center',
            }}>
              <div style={{
                opacity: (!fixedContentId && currentStepIndex === step.StepID && !isScrollamaComplete) ? 1 : 0,
                // transition: 'opacity 0.5s ease-in-out',
              }}>
                {renderStepContent(step)}
              </div>
            </div>
          </Step>
        ))}
      </Scrollama>
    </div>
      <div id="learn-more" style={{ marginTop: isScrollamaComplete ? 0 : '100vh' }}>
        {learnMoreContent}
      </div>
      <footer className='footer'>
          <div className='content-container'>
            <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' style={{ display: 'inline-block' }}>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <rect x='2' y='2' width='20' height='20' rx='5' ry='5'></rect>
                <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'></path>
                <line x1='17.5' y1='6.5' x2='17.51' y2='6.5'></line>
              </svg>
            </a>
            <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' style={{ display: 'inline-block' }}>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' strokeWidth='0.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'></path>
              </svg>
            </a>
            <a href='mailto:info@thegirlandherpiano.com' target='_blank' rel='noopener noreferrer' style={{ display: 'inline-block', marginRight: '5px' }}>
            <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'></path>
              <polyline points='22,6 12,13 2,6'></polyline>
            </svg>
          </a>
            <p>© 2024 White Room Studio Pte Ltd. All rights reserved.</p>
          </div>
        </footer>
    </>
  )
}

export default Scroller